import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';

import { AppComponent } from '@app/app.component';
import { appConfig } from '@app/app.config';
import { asBool } from '@app/shared/utils/common.util';
import { environment as env } from '@env';
import 'ag-grid-enterprise';
import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

// jspdf-autotable 5 no longer patches jsPDF on import: 23 screens call doc.autoTable(...) (e.g. Customer SOA PDF)
applyPlugin(jsPDF);

function toNum(v: unknown, d = 0): number {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
        const n = Number(v);
        return Number.isFinite(n) ? n : d;
    }
    return d;
}

if (asBool(env.sentryEnabled) && env.sentryDsn) {
    const logsEnabled = asBool(env.sentryLogEnabled, false);
    Sentry.init({
        dsn: env.sentryDsn,
        environment: env.appStage, // dev|staging|prod|local
        debug: !!asBool(env.sentryDebugEnabled),
        release: env.sentryRelease,
        enableLogs: logsEnabled, // Enable logs to be sent to Sentry
        // Performance
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(), // Session Replay,
            Sentry.dedupeIntegration(),

            // Sentry Feedback widget
            // Sentry.feedbackIntegration({ colorScheme: 'system' }),

            // send console.log, console.warn, and console.error calls as logs to Sentry
            ...(logsEnabled ? [Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] })] : []),
        ],
        tracesSampleRate: toNum(env.sentryTraces, 0.2),
        tracePropagationTargets: [
            'localhost',
            new RegExp('^' + env.apiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
        ],
        replaysSessionSampleRate: toNum(env.sentryReplaySession, 0.05),
        replaysOnErrorSampleRate: toNum(env.sentryReplayError, 1.0),

        // Scrub similar to your backend
        beforeSend(event) {
            // Don’t send noise in local dev
            if (!env.production && env.appStage === 'local') return null;

            // Basic PII/header scrubbing
            if (event.request?.headers) {
                delete event.request.headers['authorization'];
                delete event.request.headers['cookie'];
            }
            if (event.user) {
                delete event.user.ip_address;
            }
            return event;
        },
    });
}

function brandFromEnv(): void {
    // Title
    if (env.appTitle) {
        document.title = env.appTitle;
    }

    // Splash logo in index.html: the image comes from the build (SPLASH_LOGO per environment), only the alt text from env.
    const img = document.querySelector('fuse-splash-screen img') as HTMLImageElement | null;
    if (img) {
        if (env.logoTitle) {
            img.alt = env.logoTitle;
            img.title = env.logoTitle;
        }
    }

    // Favicons per env:
    const favicon16 = document.querySelector('link[rel="icon"][sizes="16x16"]') as HTMLLinkElement | null;
    if (favicon16) favicon16.href = env.favicon16 ?? 'assets/favicon/favicon-16x16.png';

    const favicon32 = document.querySelector('link[rel="icon"][sizes="32x32"]') as HTMLLinkElement | null;
    if (favicon32) favicon32.href = env.favicon32 ?? 'assets/favicon/favicon-32x32.png';

    const favicon180 = document.querySelector(
        'link[rel="apple-touch-icon"][sizes="180x180"]'
    ) as HTMLLinkElement | null;
    if (favicon180) favicon180.href = env.favicon180 ?? 'assets/favicon/apple-touch-icon.png';
}

brandFromEnv();

document.addEventListener('wheel', (e) => {
    const el = e.target as HTMLElement;
    if (
        (el instanceof HTMLInputElement && el.type === 'number') ||
        el.closest('jqxnumberinput, [jqxnumberinput], .jqx-numberinput, .custom-number-input')
    ) {
        e.preventDefault();
        e.stopPropagation();
        if (el instanceof HTMLInputElement) el.blur();
    }
}, { passive: false, capture: true });

bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
